import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import Input from "../../Utility/Input/Input";
import Button from "../../Utility/Button/Button";
import FormSubmitButton from "../../Utility/Button/FormSubmitButton";
import Typography from "../../Utility/Typography";
import Label from "../../Utility/Label/Label";
import Checkbox from "../../Utility/Checkbox/Checkbox";
import Dropdown from "../../Utility/Dropdown/Dropdown";
import { EditProfileSchema } from "../../FormsValidations/Validations";
import BackArrow from "../../Utility/BackArrow/BackArrow";
import { patch } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import toast from "react-hot-toast";
import { selectAuthMe } from "../../Redux/Slices/authSlice";
import { mapMeToEditProfileState } from "./editProfileUtils";
import { ContentLoader } from "../../Utility/Loader";

const TIMEZONE_OPTIONS = [
  "UTC",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "Europe/London",
  "Europe/Paris",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Australia/Sydney",
];

const DEFAULT_FORM = {
  name: "",
  email: "",
  phoneNumber: "",
  timezone: "UTC",
  emailNotifications: true,
};

const EditProfile = () => {
  const me = useSelector(selectAuthMe);
  const profileResolved = useSelector((state) => state.auth?.profileResolved);
  const fetchMeLoading = useSelector((state) => state.auth?.loading?.fetchMe);
  const profileLoading = me == null && (!profileResolved || fetchMeLoading);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [userMeta, setUserMeta] = useState({ role: "", team: "", lastLogin: "" });

  const location = useLocation();
  const prevPath = location.state?.previousPath || "/dashboard";

  useEffect(() => {
    const mapped = mapMeToEditProfileState(me);
    if (!mapped) return;
    setFormData(mapped.formData);
    setUserMeta(mapped.userMeta);
  }, [me]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    const val = type === "checkbox" ? e.target.checked : value;
    setFormData((prev) => ({ ...prev, [name]: val }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = async () => {
    try {
      await EditProfileSchema.validate(formData, { abortEarly: false });
      setErrors({});
      return true;
    } catch (err) {
      const validationErrors = {};
      err.inner?.forEach((e) => {
        validationErrors[e.path] = e.message;
      });
      setErrors(validationErrors);
      return false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!(await validateForm())) return;
    setLoading(true);
    try {
      // Split "Full Name" → firstName + lastName
      const nameParts = formData.name.trim().split(/\s+/);
      const firstName = nameParts[0] ?? "";
      const lastName = nameParts.slice(1).join(" ") || undefined;

      await patch(API_ENDPOINTS.USERS.ME_UPDATE, {
        firstName,
        lastName,
        phoneNumber: formData.phoneNumber || undefined,
        timezone: formData.timezone || undefined,
        emailNotifications: formData.emailNotifications,
      });
    } catch (error) {
      toast.error(error?.response?.data?.error?.message ?? "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    window.history.back();
  };

  const avatarLetter = (formData.name || "U").charAt(0).toUpperCase();

  return (
    <div className="p-6">
      <div className="max-w-2xl mx-auto">
        <div className="d-flex align-items-center gap-2 mb-4">
          <Typography variant="h4" className="text-theme-primary mb-0">
            <BackArrow to={prevPath} /> Edit Profile
          </Typography>
        </div>

        <ContentLoader loading={profileLoading} className="min-h-[28rem]" dimContent={false}>
          {!profileLoading ? (
            <div className="relative flex flex-col min-w-0 bg-surface-primary rounded-md border border-border-primary shadow-sm">
              {/* Profile header: Avatar, name, role · team, last login */}
              <div className="flex flex-wrap items-center gap-4 p-6 pb-5 border-b border-border-primary">
                <div className="bg-theme-primary w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
                  {avatarLetter}
                </div>
                <div className="min-w-0">
                  <Typography variant="h5" className="text-text-primary font-semibold mb-0.5">
                    {formData.name || "—"}
                  </Typography>
                  <Typography variant="text2" className="text-text-secondary">
                    {userMeta.role && userMeta.team
                      ? `${userMeta.role} · ${userMeta.team}`
                      : userMeta.role || userMeta.team || "—"}
                  </Typography>
                  <Typography variant="text2" className="text-text-secondary mt-0.5">
                    Last login: {userMeta.lastLogin || "—"}
                  </Typography>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex-1 p-6">
                {/* Personal Information */}
                <Typography
                  variant="text2"
                  className="text-text-secondary font-semibold uppercase tracking-wide mb-3 block"
                >
                  Personal Information
                </Typography>
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                      error={errors.name}
                      className={`w-full border rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                        errors.name ? "border-badge-danger" : "border-border-primary"
                      }`}
                      noWrapper={true}
                    />
                    {errors.name && (
                      <Typography variant="text2" className="text-badge-danger mt-1 block">
                        {errors.name}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      readOnly
                      value={formData.email}
                      noWrapper={true}
                      className="w-full border border-border-primary rounded-sm px-3 py-1.5 text-text-secondary bg-surface-secondary cursor-not-allowed"
                    />
                    <Typography variant="text2" className="text-text-secondary mt-1 block">
                      (read-only)
                    </Typography>
                  </div>
                  <div>
                    <Label htmlFor="phoneNumber">Phone Number</Label>
                    <Input
                      type="tel"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                      error={errors.phoneNumber}
                      className={`w-full border rounded-sm px-3 py-1.5 text-text-primary bg-surface-primary ${
                        errors.phoneNumber ? "border-badge-danger" : "border-border-primary"
                      }`}
                      noWrapper={true}
                    />
                    {errors.phoneNumber && (
                      <Typography variant="text2" className="text-badge-danger mt-1 block">
                        {errors.phoneNumber}
                      </Typography>
                    )}
                  </div>
                </div>

                {/* Security */}
                <Typography
                  variant="text2"
                  className="text-text-secondary font-semibold uppercase tracking-wide mb-3 block"
                >
                  Security
                </Typography>
                <div className="space-y-4 mb-6 border-b border-border-primary pb-6">
                  <div className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <Link
                      to="/change-password"
                      state={{ previousPath: location.pathname }}
                      className="text-sm font-medium text-theme-primary hover:underline"
                    >
                      Change Password
                    </Link>
                  </div>
                </div>

                {/* Preferences */}
                <Typography
                  variant="text2"
                  className="text-text-secondary font-semibold uppercase tracking-wide mb-3 block"
                >
                  Preferences
                </Typography>
                <div className="space-y-4 mb-6">
                  <div>
                    <Label htmlFor="timezone">Timezone</Label>
                    <Dropdown
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      options={TIMEZONE_OPTIONS}
                      placeholder="Select timezone"
                      error={errors.timezone}
                      searchable={true}
                      className="text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="emailNotifications"
                      name="emailNotifications"
                      checked={formData.emailNotifications}
                      onChange={handleChange}
                      label="Email Notifications"
                    />
                  </div>
                </div>

                {/* Footer buttons */}
                <div className="flex justify-end gap-2 pt-2 border-t border-border-primary">
                  <Button
                    type="button"
                    variant="danger"
                    size="md"
                    disabled={loading}
                    onClick={handleCancel}
                    className="btn-reset"
                  >
                    Cancel
                  </Button>
                  <FormSubmitButton
                    loading={loading}
                    label="Save Changes"
                    loadingText="Saving..."
                    idleIconClass=""
                  />
                </div>
              </form>
            </div>
          ) : null}
        </ContentLoader>
      </div>
    </div>
  );
};

export default EditProfile;
